import { DocumentGroup, UploadedDocumentInfoDocumentType } from '@urbanx/agx.campaigns.client'
import { DocumentTypesMap } from '@urbanx/agx-ui-components'
import {
  AgxCollapsible,
  Icon_LoaderOutline,
  AgxButton,
  AgxSpacer,
} from '@urbanx/agx.ui-components2'
import { useContext, useState } from 'react'
import { editFile, StaffGetUploadedFileLink } from 'services'
import { AuthContext } from '@urbanx/agx.auth.client'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { splitPascalCase } from 'utils/stringUtils'
import './TasksFilesList.scss'

interface FilesListProps {
  files?: DocumentGroup[]
  id: string
}

const openFilePicker = (): Promise<File | null> => {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.onchange = () => {
      const file = input.files?.[0] ?? null
      resolve(file)
    }
    input.click()
  })
}

interface TaskDocumentItemProps {
  id: string
  fullPath: string
  documentId?: string
  documentType?: UploadedDocumentInfoDocumentType | string | null
  fileName?: string | null
  title?: string | null
  getAuthToken: () => Promise<string>
}

const RESPONSE_CONTENT_DISPOSITION_INLINE = '&response-content-disposition=inline'
const GOOGLE_VIEWER_BASE = 'https://docs.google.com/gview?embedded=true&url='

const getTaskDocumentTitle = (
  title?: string | null,
  documentType?: UploadedDocumentInfoDocumentType | string | null,
  fileName?: string | null
) => {
  if (title) return title

  const documentTypeTitle = documentType
    ? DocumentTypesMap[documentType as keyof typeof DocumentTypesMap]
    : undefined

  return documentTypeTitle || splitPascalCase(documentType ?? '') || fileName || ''
}

const TaskDocumentItem = ({
  id,
  fullPath,
  documentId,
  documentType,
  fileName,
  title,
  getAuthToken,
}: TaskDocumentItemProps) => {
  const queryClient = useQueryClient()
  const [isUploading, setIsUploading] = useState(false)

  const {
    data: fileUrl,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['file-link', fullPath],
    enabled: !!fullPath,
    queryFn: async () => StaffGetUploadedFileLink(fullPath, getAuthToken),
  })

  const normalizedUrl = fileUrl
    ? fileUrl + RESPONSE_CONTENT_DISPOSITION_INLINE
    : undefined
  const googleViewerUrl = normalizedUrl
    ? GOOGLE_VIEWER_BASE + encodeURIComponent(normalizedUrl)
    : undefined

  return (
    <AgxCollapsible
      key={documentId ?? fullPath}
      title={getTaskDocumentTitle(title, documentType, fileName)}
      enableToggle
      showBorder
      iconPosition="none"
      variant="withoutDivider"
      headerPadding="sm"
      isContentFit
      backgroundColor="white"
      maxTitleLegth={25}
      actionContent={
        <AgxSpacer direction="vertical">
          <AgxButton
            id={'upload_btn'}
            variant="link"
            disabled={isUploading}
            text={isUploading ? 'Uploading…' : 'Upload new file'}
            onClick={async () => {
              const selectedFile = await openFilePicker()
              if (!selectedFile) return

              const token = await getAuthToken()

              const formData = new FormData()
              formData.append('file', selectedFile)

              try {
                setIsUploading(true)

                await editFile(
                  token,
                  formData,
                  id,
                  documentType as UploadedDocumentInfoDocumentType,
                  fullPath
                )
              } finally {
                setIsUploading(false)

                queryClient.invalidateQueries({
                  queryKey: ['file-link', fullPath],
                })
              }
            }}
          />
        </AgxSpacer>
      }
      content={
        <div className="tasks-files-list__preview-content">
          {isLoading && <div>Loading file…</div>}
          {!isLoading && isError && (
            <div className="tasks-files-list__preview-error">
              Failed to load file.
            </div>
          )}
          {!isLoading && !isUploading && googleViewerUrl ? (
            <iframe
              src={googleViewerUrl}
              className="tasks-files-list__preview-frame"
            />
          ) : (
            <Icon_LoaderOutline />
          )}
        </div>
      }
    />
  )
}

const TasksFilesList = ({ files = [], id }: FilesListProps) => {
  const { getAuthToken } = useContext(AuthContext)

  return (
    <AgxSpacer direction="vertical" gap="spacing-sm" fill>
      {files.map((group) =>
        group.documents?.map((doc) => {
          const fullPath = doc.containerFilePath ?? ''

          return (
            <TaskDocumentItem
              key={doc.documentId ?? fullPath}
              id={id}
              fullPath={fullPath}
              documentId={doc.documentId}
              documentType={doc.documentType}
              fileName={doc.fileName}
              title={doc.title}
              getAuthToken={getAuthToken}
            />
          )
        })
      )}
    </AgxSpacer>
  )
}

export default TasksFilesList
